import { useEffect, useRef, useState } from "react";

/**
 * 相機掃會員條碼：會員卡頁預設顯示的一維條碼（CODE128）與點開後的 QR code 都能讀。
 *
 * 一律先用 getUserMedia 開相機：瀏覽器在這一步才會跳出相機權限詢問（讀條碼與 QR 共用同一個權限）。
 * 辨識優先用瀏覽器內建的 BarcodeDetector（Android Chrome），
 * 沒有的瀏覽器（iOS Safari、LINE 內建瀏覽器）改用 ZXing 從畫面逐格辨識。
 */
export const isCameraSupported = () =>
	typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

const SCAN_INTERVAL_MS = 250;
// 逐格辨識前先縮圖，手機相機原始解析度太高會讓 ZXing 每格處理太久
const MAX_DECODE_WIDTH = 960;

/** 把 getUserMedia 的錯誤轉成給 PG 看的說明 */
export const cameraErrorMessage = (error) => {
	switch (error?.name) {
		case "NotAllowedError":
		case "SecurityError":
			return "相機權限被拒絕。請到瀏覽器（或手機設定）允許此網站使用相機後重新整理，或改用手動輸入";
		case "NotFoundError":
		case "OverconstrainedError":
			return "找不到可用的相機，請改用手動輸入";
		case "NotReadableError":
			return "相機正被其他 App 使用中，請關閉後再試";
		default:
			return "無法開啟相機，請改用手動輸入";
	}
};

const createNativeDetector = async () => {
	if (!("BarcodeDetector" in window)) return null;

	// 只帶這台裝置支援的格式，不支援的格式會讓建構子直接丟錯
	const wanted = ["qr_code", "code_128"];
	const supported = await window.BarcodeDetector.getSupportedFormats?.();
	const formats = supported ? wanted.filter((f) => supported.includes(f)) : wanted;
	if (formats.length === 0) return null;

	const detector = new window.BarcodeDetector({ formats });
	return async (video) => {
		const codes = await detector.detect(video);
		return codes[0]?.rawValue;
	};
};

// ZXing 約 65 kB（gzip），只在需要時才載入，不打包進會員中心共用的主程式
const createZxingDetector = async () => {
	const {
		BarcodeFormat,
		BinaryBitmap,
		DecodeHintType,
		HTMLCanvasElementLuminanceSource,
		HybridBinarizer,
		MultiFormatReader,
	} = await import("@zxing/library");

	const reader = new MultiFormatReader();
	const hints = new Map();
	hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE, BarcodeFormat.CODE_128]);
	// 允許較慢但較準的辨識，一維條碼橫放或直放都會試
	hints.set(DecodeHintType.TRY_HARDER, true);
	reader.setHints(hints);

	const canvas = document.createElement("canvas");
	const context = canvas.getContext("2d", { willReadFrequently: true });

	return async (video) => {
		const { videoWidth, videoHeight } = video;
		if (!videoWidth || !videoHeight) return null;

		const scale = Math.min(1, MAX_DECODE_WIDTH / videoWidth);
		canvas.width = Math.round(videoWidth * scale);
		canvas.height = Math.round(videoHeight * scale);
		context.drawImage(video, 0, 0, canvas.width, canvas.height);

		try {
			const bitmap = new BinaryBitmap(new HybridBinarizer(new HTMLCanvasElementLuminanceSource(canvas)));
			return reader.decodeWithState(bitmap).getText();
		} catch {
			// 這一格沒有讀到（NotFoundException 等），下一格再試
			return null;
		} finally {
			reader.reset();
		}
	};
};

const CameraScanner = ({ open, onDetected, onClose, onError }) => {
	const videoRef = useRef(null);
	const [starting, setStarting] = useState(true);

	useEffect(() => {
		if (!open) return;

		let stream = null;
		let stopped = false;
		let timer = null;

		const start = async () => {
			try {
				// 第一次會在這裡跳出相機權限詢問
				stream = await navigator.mediaDevices.getUserMedia({
					video: { facingMode: "environment" },
					audio: false,
				});
				if (stopped) {
					stream.getTracks().forEach((track) => track.stop());
					return;
				}

				const video = videoRef.current;
				video.srcObject = stream;
				await video.play();
				setStarting(false);

				const detect = (await createNativeDetector()) ?? (await createZxingDetector());
				const tick = async () => {
					if (stopped) return;
					try {
						const value = (await detect(video))?.trim();
						if (value) {
							onDetected(value);
							return;
						}
					} catch {
						// 單一 frame 辨識失敗忽略，下一個 frame 再試
					}
					timer = setTimeout(tick, SCAN_INTERVAL_MS);
				};
				tick();
			} catch (e) {
				if (!stopped) onError?.(e);
			}
		};

		setStarting(true);
		start();

		return () => {
			stopped = true;
			clearTimeout(timer);
			stream?.getTracks().forEach((track) => track.stop());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	if (!open) return null;

	return (
		<div className="fixed inset-0 z-50 flex flex-col bg-black">
			<div className="relative flex-1">
				<video ref={videoRef} className="h-full w-full object-cover" playsInline muted />
				{/* 掃描框做成橫長方形，條碼與 QR code 都放得進去 */}
				<div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
					<div className="h-[50vw] max-h-[260px] w-[80vw] max-w-[420px] rounded-lg border-4 border-white/80" />
					<p className="mt-4 rounded-full bg-black/50 px-4 py-1 text-[14px] text-white">
						對準會員卡上的條碼或 QR code
					</p>
				</div>
				{starting && (
					<p className="absolute inset-x-0 top-1/3 text-center text-white">相機啟動中…</p>
				)}
				<button
					type="button"
					aria-label="關閉相機"
					className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-black/50"
					onClick={onClose}
				>
					<svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
						<path d="M6 6l12 12M18 6L6 18" />
					</svg>
				</button>
			</div>
			<button
				className="noto-sans-tc-medium bg-white py-4 text-[16px] text-[#292929]"
				onClick={onClose}
			>
				取消
			</button>
		</div>
	);
};

export default CameraScanner;
